/**
 * Web proactive token refresh (permanent-session-continuity design §4.6 Web, FR-6.1–6.5).
 *
 * - Refreshes ~5 min before the access token's `exp` (skew-adjusted).
 * - Refreshes on tab visibility regain / `online` when `exp` is within 60 min.
 * - Re-arms whenever the access token changes: another tab (`storage`), this tab's
 *   login / institute switch (`myams:session-changed`), or any refresh
 *   (`myams:access-token-refreshed`).
 * - Uses the shared single-flight refresh. Proactive outcomes NEVER log out:
 *   `success` re-arms, `transient` retries once after 60 s, `terminal` stops the
 *   timer and leaves the decision to the reactive interceptor.
 *
 * Every entry point is wrapped in try/catch and never throws.
 */
import { refreshAccessTokenSingleFlight } from './api';
import {
  decodeJwtPayload,
  getClockSkewMs,
  computeProactiveRefreshDelayMs,
  isWithinRefreshWindow,
  PROACTIVE_LEAD_MS,
  RESUME_WINDOW_MS,
  MIN_PROACTIVE_INTERVAL_MS,
  MIN_TIMER_DELAY_MS,
  MAX_TIMER_DELAY_MS,
} from '../utils/jwtClock';

const LOG = '[tokenKeepAlive]';

function readTokens() {
  try {
    return {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
    };
  } catch (e) {
    console.warn(`${LOG} token read failed:`, e?.message || e);
    return { accessToken: null, refreshToken: null };
  }
}

function readExpMs(accessToken) {
  const exp = decodeJwtPayload(accessToken)?.exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
}

/**
 * Starts the keep-alive. Returns `stop()`, which clears the timer and all listeners.
 * Never throws.
 */
export function startTokenKeepAlive() {
  let timerId = null;
  let stopped = false;
  let lastAttemptMs = 0;
  let transientRetryUsed = false;

  const clearTimer = () => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  /**
   * (Re)arms the single timer from the current token. `minDelayMs` keeps skip paths
   * from arming an immediate timer (no spin). No timer without a refresh token or exp.
   */
  const schedule = ({ minDelayMs = 0 } = {}) => {
    try {
      clearTimer();
      if (stopped) return;
      const { accessToken, refreshToken } = readTokens();
      if (!accessToken || !refreshToken) return;
      const expMs = readExpMs(accessToken);
      if (expMs === null) return;
      let delay = computeProactiveRefreshDelayMs({
        expMs,
        nowMs: Date.now(),
        skewMs: getClockSkewMs(),
        leadMs: PROACTIVE_LEAD_MS,
      });
      if (delay === null) return;
      const min = Number.isFinite(minDelayMs) && minDelayMs > 0 ? minDelayMs : 0;
      delay = Math.max(delay, min);
      if (delay <= 0) {
        timerId = setTimeout(() => { void maybeRefresh('timer'); }, 0);
        return;
      }
      if (delay > MAX_TIMER_DELAY_MS) {
        // Truncated: wake up and recompute instead of refreshing.
        timerId = setTimeout(() => { schedule(); }, MAX_TIMER_DELAY_MS);
        return;
      }
      timerId = setTimeout(
        () => { void maybeRefresh('timer'); },
        Math.max(delay, MIN_TIMER_DELAY_MS)
      );
    } catch (e) {
      console.warn(`${LOG} schedule failed:`, e?.message || e);
    }
  };

  const maybeRefresh = async (reason) => {
    try {
      // Event-driven calls (visible/online) must not drop the pending timer: every
      // re-arm below goes through clearTimer(), so at most one timer exists.
      if (stopped) return;
      const { accessToken, refreshToken } = readTokens();
      // Nothing to refresh with: arm nothing; login listeners re-arm.
      if (!accessToken || !refreshToken) return;
      const expMs = readExpMs(accessToken);
      if (expMs === null) return;

      const sinceLast = Date.now() - lastAttemptMs;
      if (sinceLast < MIN_PROACTIVE_INTERVAL_MS) {
        schedule({ minDelayMs: MIN_PROACTIVE_INTERVAL_MS - sinceLast });
        return;
      }

      const windowMs = reason === 'timer' || reason === 'timer-retry' ? PROACTIVE_LEAD_MS : RESUME_WINDOW_MS;
      if (!isWithinRefreshWindow({ expMs, nowMs: Date.now(), skewMs: getClockSkewMs(), windowMs })) {
        // Token is not near expiry (possibly replaced by another tab / a login).
        schedule();
        return;
      }

      lastAttemptMs = Date.now();
      const result = await refreshAccessTokenSingleFlight();
      if (stopped) return;
      const status = result?.status;
      if (status === 'success') {
        transientRetryUsed = false;
        schedule();
      } else if (status === 'transient') {
        if (!transientRetryUsed) {
          transientRetryUsed = true;
          clearTimer();
          timerId = setTimeout(
            () => { void maybeRefresh('timer-retry'); },
            Math.max(MIN_PROACTIVE_INTERVAL_MS, MIN_TIMER_DELAY_MS)
          );
        } else {
          // Second consecutive transient: stop; foreground/online/reactive paths take over.
          clearTimer();
          console.info(`${LOG} refresh transient twice; waiting for next trigger`);
        }
      } else {
        // Terminal (or unknown): never log out here; the reactive interceptor decides.
        clearTimer();
        console.info(`${LOG} refresh terminal; proactive refresh stopped`);
      }
    } catch (e) {
      console.warn(`${LOG} maybeRefresh(${reason}) failed:`, e?.message || e);
    }
  };

  const onVisibility = () => {
    try {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      void maybeRefresh('visible');
    } catch (e) {
      console.warn(`${LOG} visibilitychange failed:`, e?.message || e);
    }
  };
  const onOnline = () => {
    try { void maybeRefresh('online'); } catch (e) { console.warn(`${LOG} online failed:`, e?.message || e); }
  };
  const onStorage = (e) => {
    try { if (e?.key === 'accessToken' || e?.key === 'refreshToken') schedule(); }
    catch (err) { console.warn(`${LOG} storage failed:`, err?.message || err); }
  };
  const onTokenChanged = () => {
    try { transientRetryUsed = false; schedule(); }
    catch (e) { console.warn(`${LOG} token-change reschedule failed:`, e?.message || e); }
  };

  try {
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
      window.addEventListener('storage', onStorage);
      window.addEventListener('myams:session-changed', onTokenChanged);
      window.addEventListener('myams:access-token-refreshed', onTokenChanged);
    }
  } catch (e) {
    console.warn(`${LOG} listener registration failed:`, e?.message || e);
  }

  schedule();

  return function stop() {
    try {
      stopped = true;
      clearTimer();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('storage', onStorage);
        window.removeEventListener('myams:session-changed', onTokenChanged);
        window.removeEventListener('myams:access-token-refreshed', onTokenChanged);
      }
    } catch (e) {
      console.warn(`${LOG} stop failed:`, e?.message || e);
    }
  };
}

export default startTokenKeepAlive;
